import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiErrorDetail, formatMoney } from "@/lib/api";
import {
  ShieldCheck, LogIn, Plus, Pause, Play, Trash2, RefreshCw, KeyRound,
  Building2, Users, CalendarCheck, AlertTriangle, LogOut, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const SUPER_TOKEN_KEY = "dc_super_token";

const superApi = axios.create({ baseURL: API_BASE });
superApi.interceptors.request.use((config) => {
  const tk = localStorage.getItem(SUPER_TOKEN_KEY);
  if (tk) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tk}`;
  }
  return config;
});

function SuperLogin({ onLogged }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await superApi.post("/super/auth/login", { email, password });
      localStorage.setItem(SUPER_TOKEN_KEY, data.access_token);
      onLogged(data.super_admin);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Credenciales inválidas");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm" data-testid="super-login">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-sm bg-foreground text-background inline-flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="label-eyebrow mb-2">Panel administrador</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">DentCloud</h1>
        </div>
        <form onSubmit={submit} className="card-flat p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Correo super-admin</label>
            <input type="email" required autoFocus className="input-flat"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="super-email" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Contraseña</label>
            <input type="password" required className="input-flat"
              value={password} onChange={(e) => setPassword(e.target.value)} data-testid="super-password" />
          </div>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-sm p-2">{error}</div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="super-login-btn">
            <LogIn className="w-4 h-4" /> {loading ? "Ingresando…" : "Ingresar"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NewTenantForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    slug: "", name: "", plan: "basic", expires_at: "",
    admin_email: "", admin_password: "", admin_name: "Administrador",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.expires_at) delete payload.expires_at;
      await superApi.post("/super/tenants", payload);
      toast.success("Clínica creada");
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="card-flat p-5 space-y-3" data-testid="new-tenant-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Slug / dominio *</label>
          <input required className="input-flat font-mono" placeholder="clinicasonrisas"
            value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
            data-testid="t-slug" />
          <div className="text-[10px] text-muted-foreground mt-1">URL: <span className="font-mono">/c/{form.slug || "clinica"}</span></div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Nombre clínica *</label>
          <input required className="input-flat" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Plan</label>
          <select className="input-flat" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="t-plan">
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Vence (opcional)</label>
          <input type="date" className="input-flat font-mono"
            value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            data-testid="t-expires" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Email admin *</label>
          <input type="email" required className="input-flat"
            value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="t-admin-email" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Password admin *</label>
          <input type="text" required className="input-flat font-mono" minLength="6"
            value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="t-admin-pwd" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Nombre del administrador</label>
          <input className="input-flat" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Notas internas</label>
          <textarea rows={2} className="input-flat h-auto py-2"
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary" data-testid="t-save">
          <Plus className="w-4 h-4" /> {saving ? "Creando…" : "Crear clínica"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-sm">Cancelar</button>
      </div>
    </form>
  );
}

function TenantsList({ tenants, onChange }) {
  const today = new Date().toISOString().slice(0, 10);

  const suspend = async (t) => {
    if (!window.confirm(`¿Suspender ${t.name}? Los usuarios no podrán ingresar.`)) return;
    try { await superApi.post(`/super/tenants/${t.id}/suspend`); toast.success("Suspendida"); onChange(); }
    catch (e) { toast.error("Error"); }
  };
  const activate = async (t) => {
    try { await superApi.post(`/super/tenants/${t.id}/activate`); toast.success("Activada"); onChange(); }
    catch (e) { toast.error("Error"); }
  };
  const extend = async (t) => {
    const days = window.prompt("¿Cuántos días extender la licencia?", "30");
    if (!days) return;
    try {
      const { data } = await superApi.post(`/super/tenants/${t.id}/extend?days=${parseInt(days, 10) || 30}`);
      toast.success(`Vence: ${data.expires_at}`); onChange();
    } catch { toast.error("Error"); }
  };
  const resetPwd = async (t) => {
    const np = window.prompt(`Nueva contraseña para ${t.admin_email}:`);
    if (!np || np.length < 6) return;
    try {
      await superApi.post(`/super/tenants/${t.id}/reset-admin-password?new_password=${encodeURIComponent(np)}`);
      toast.success("Contraseña actualizada");
    } catch { toast.error("Error"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar el registro de ${t.name}?\n\nLos datos pueden conservarse o borrarse.`)) return;
    const drop = window.confirm("¿Borrar TAMBIÉN todos los datos de la clínica? (irreversible)\n\nClick OK para borrar todo, Cancelar para conservar datos.");
    try { await superApi.delete(`/super/tenants/${t.id}?drop_data=${drop}`); toast.success("Eliminada"); onChange(); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="card-flat overflow-hidden" data-testid="tenants-list">
      <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
        <div className="col-span-3">Clínica</div>
        <div className="col-span-1">Plan</div>
        <div className="col-span-2">Vence</div>
        <div className="col-span-1 text-right">Pacientes</div>
        <div className="col-span-1 text-right">Citas</div>
        <div className="col-span-1">Estado</div>
        <div className="col-span-3 text-right">Acciones</div>
      </div>
      {tenants.length === 0 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">Aún no hay clínicas. Crea la primera arriba.</div>}
      {tenants.map((t) => {
        const expiringSoon = t.expires_at && t.expires_at <= today;
        return (
          <div key={t.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/20" data-testid={`tenant-row-${t.id}`}>
            <div className="col-span-3 min-w-0">
              <div className="font-bold text-sm truncate">{t.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">/c/{t.slug}</div>
              <div className="text-[10px] text-muted-foreground truncate mt-0.5">{t.admin_email}</div>
            </div>
            <div className="col-span-1 text-xs uppercase tracking-widest font-bold">{t.plan}</div>
            <div className="col-span-2 font-mono text-xs">
              {t.expires_at ? <span className={expiringSoon ? "text-destructive font-bold" : ""}>{t.expires_at}</span> : <span className="text-muted-foreground">— sin vencimiento</span>}
            </div>
            <div className="col-span-1 text-right font-mono text-sm">{t.stats?.patients ?? 0}</div>
            <div className="col-span-1 text-right font-mono text-sm">{t.stats?.appointments ?? 0}</div>
            <div className="col-span-1">
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${
                t.expired ? "bg-destructive/15 text-destructive" :
                t.status === "active" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                {t.expired ? "expirada" : t.status}
              </span>
            </div>
            <div className="col-span-3 flex justify-end items-center gap-1">
              <button onClick={() => extend(t)} title="Extender licencia" className="p-2 text-muted-foreground hover:text-primary" data-testid={`extend-${t.id}`}>
                <Calendar className="w-4 h-4" />
              </button>
              <button onClick={() => resetPwd(t)} title="Reset contraseña admin" className="p-2 text-muted-foreground hover:text-primary">
                <KeyRound className="w-4 h-4" />
              </button>
              {t.status === "active" ? (
                <button onClick={() => suspend(t)} title="Suspender" className="p-2 text-muted-foreground hover:text-destructive" data-testid={`suspend-${t.id}`}>
                  <Pause className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => activate(t)} title="Activar" className="p-2 text-muted-foreground hover:text-primary" data-testid={`activate-${t.id}`}>
                  <Play className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => remove(t)} title="Eliminar" className="p-2 text-muted-foreground hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const checkAuth = async () => {
    const tk = localStorage.getItem(SUPER_TOKEN_KEY);
    if (!tk) { setMe(false); setLoading(false); return; }
    try {
      const { data } = await superApi.get("/super/auth/me");
      setMe(data);
      await load();
    } catch {
      localStorage.removeItem(SUPER_TOKEN_KEY);
      setMe(false);
    } finally { setLoading(false); }
  };

  const load = async () => {
    try {
      const { data } = await superApi.get("/super/tenants");
      setTenants(data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { checkAuth(); }, []); // eslint-disable-line

  const logout = async () => {
    try { await superApi.post("/super/auth/logout"); } catch {}
    localStorage.removeItem(SUPER_TOKEN_KEY);
    setMe(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm font-mono text-muted-foreground">Cargando…</div>
    </div>
  );

  if (!me) return <SuperLogin onLogged={(u) => { setMe(u); load(); }} />;

  const totals = tenants.reduce((acc, t) => {
    acc.users += t.stats?.users || 0;
    acc.patients += t.stats?.patients || 0;
    acc.appointments += t.stats?.appointments || 0;
    if (t.status === "active" && !t.expired) acc.active += 1;
    if (t.status === "suspended") acc.suspended += 1;
    if (t.expired) acc.expired += 1;
    return acc;
  }, { users: 0, patients: 0, appointments: 0, active: 0, suspended: 0, expired: 0 });

  return (
    <div className="min-h-screen bg-background p-6 sm:p-10" data-testid="super-dashboard">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Panel super-admin
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">DentCloud</h1>
          <p className="text-sm text-muted-foreground mt-2">Gestión de licencias y clínicas registradas en la plataforma.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground font-mono">{me.email}</div>
          <button onClick={logout} className="px-4 h-10 rounded-sm border border-border hover:bg-destructive/10 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="super-logout">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="card-flat p-4">
          <div className="label-eyebrow flex items-center gap-1"><Building2 className="w-3 h-3" /> Clínicas</div>
          <div className="font-mono font-bold text-3xl mt-1">{tenants.length}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow text-primary">Activas</div>
          <div className="font-mono font-bold text-3xl mt-1 text-primary">{totals.active}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow text-destructive">Expiradas</div>
          <div className="font-mono font-bold text-3xl mt-1 text-destructive">{totals.expired}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow flex items-center gap-1"><Users className="w-3 h-3" /> Pacientes totales</div>
          <div className="font-mono font-bold text-3xl mt-1">{totals.patients}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> Citas totales</div>
          <div className="font-mono font-bold text-3xl mt-1">{totals.appointments}</div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display font-bold text-2xl tracking-tight">Clínicas registradas</h2>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setCreating((v) => !v)} className="btn-primary" data-testid="new-tenant-btn">
            <Plus className="w-4 h-4" /> {creating ? "Cancelar" : "Nueva clínica"}
          </button>
        </div>
      </div>

      {creating && <div className="mb-4"><NewTenantForm onCreated={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></div>}

      <TenantsList tenants={tenants} onChange={load} />
    </div>
  );
}
